import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Star, CheckCircle, XCircle, Trash2, Award, Clock, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TestimonialManager = () => {
  const navigate = useNavigate();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ client_name: '', testimonial_text: '', designation: '', project_type: '', rating: 5, featured: false });
  const [adding, setAdding] = useState(false);
  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    if (!token) { navigate('/admin'); return; }
    loadTestimonials();
  }, [token, navigate]);

  const loadTestimonials = async () => {
    try {
      const res = await axios.get(`${API}/testimonials/admin`, { headers: { Authorization: `Bearer ${token}` } });
      setTestimonials(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleApproval = async (id) => {
    try {
      const res = await axios.put(`${API}/testimonials/${id}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setTestimonials(prev => prev.map(t => t.id === id ? { ...t, approved: res.data.approved } : t));
      toast.success(res.data.approved ? 'Testimonial approved' : 'Testimonial hidden');
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleToggleFeatured = async (id) => {
    try {
      const res = await axios.put(`${API}/testimonials/${id}/feature`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setTestimonials(prev => prev.map(t => t.id === id ? { ...t, featured: res.data.featured } : t));
      toast.success(res.data.featured ? 'Marked as featured' : 'Removed from featured');
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    try {
      await axios.delete(`${API}/testimonials/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setTestimonials(prev => prev.filter(t => t.id !== id));
      toast.success('Testimonial deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([k, v]) => formData.append(k, v));
      await axios.post(`${API}/testimonials`, formData, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Testimonial added');
      setShowAdd(false);
      setForm({ client_name: '', testimonial_text: '', designation: '', project_type: '', rating: 5, featured: false });
      loadTestimonials();
    } catch (err) {
      toast.error('Failed to add');
    } finally {
      setAdding(false);
    }
  };

  const filtered = testimonials.filter(t => {
    if (filter === 'pending') return !t.approved;
    if (filter === 'approved') return t.approved;
    if (filter === 'featured') return t.featured;
    return true;
  });

  const pendingCount = testimonials.filter(t => !t.approved).length;

  return (
    <div className="min-h-screen pt-24 sm:pt-32 pb-16 sm:pb-24" data-testid="testimonial-manager-page">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24">
        <button onClick={() => navigate('/admin/dashboard')}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-accent mb-2 transition-colors"
          data-testid="back-to-dashboard">
          <ArrowLeft size={16} /> Back to Dashboard
        </button>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl sm:text-4xl font-light tracking-tight" data-testid="testimonial-manager-title">
              Testimonials
            </h1>
            {pendingCount > 0 && (
              <p className="font-sans text-sm text-amber-600 mt-1">
                <Clock size={13} className="inline mr-1" />{pendingCount} pending approval
              </p>
            )}
          </div>
          <Button onClick={() => setShowAdd(!showAdd)} className="bg-accent hover:bg-accent/90" data-testid="add-testimonial-btn">
            {showAdd ? 'Cancel' : '+ Add Testimonial'}
          </Button>
        </div>

        {/* Add Form */}
        {showAdd && (
          <form onSubmit={handleAdd} className="bg-card border border-border rounded-lg p-5 mb-6 space-y-3" data-testid="add-testimonial-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">Name *</Label>
                <Input value={form.client_name} onChange={e => setForm({ ...form, client_name: e.target.value })} required /></div>
              <div><Label className="text-xs">Designation</Label>
                <Input value={form.designation} onChange={e => setForm({ ...form, designation: e.target.value })} placeholder="Art Collector, City" /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">Project Type</Label>
                <Input value={form.project_type} onChange={e => setForm({ ...form, project_type: e.target.value })} placeholder="Commission, Purchase..." /></div>
              <div><Label className="text-xs">Rating</Label>
                <select value={form.rating} onChange={e => setForm({ ...form, rating: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm">
                  {[5, 4, 3, 2, 1].map(r => <option key={r} value={r}>{r} Stars</option>)}
                </select></div>
            </div>
            <div><Label className="text-xs">Testimonial Text *</Label>
              <Textarea value={form.testimonial_text} onChange={e => setForm({ ...form, testimonial_text: e.target.value })} rows={3} required /></div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={form.featured} onChange={e => setForm({ ...form, featured: e.target.checked })} id="feat" />
              <Label htmlFor="feat" className="text-xs cursor-pointer">Featured</Label>
            </div>
            <Button type="submit" disabled={adding} className="bg-accent hover:bg-accent/90" data-testid="save-testimonial-btn">
              {adding ? 'Adding...' : 'Add Testimonial'}
            </Button>
          </form>
        )}

        {/* Filters */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {['all', 'pending', 'approved', 'featured'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-colors ${filter === f ? 'bg-accent text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
              data-testid={`filter-${f}`}>
              {f} {f === 'pending' && pendingCount > 0 ? `(${pendingCount})` : ''}
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div className="space-y-4">{[1, 2, 3].map(i => <div key={i} className="h-24 bg-muted rounded-lg animate-pulse" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <User size={32} className="mx-auto text-muted-foreground/30 mb-3" />
            <p className="font-sans text-muted-foreground text-sm">No testimonials in this category</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(t => (
              <div key={t.id} className={`bg-card border rounded-lg p-4 sm:p-5 flex gap-4 ${!t.approved ? 'border-amber-300/50 bg-amber-50/5' : 'border-border'}`}
                data-testid={`testimonial-item-${t.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-sans text-sm font-semibold">{t.client_name}</span>
                    {t.designation && <span className="text-xs text-muted-foreground">· {t.designation}</span>}
                    {t.visitor_email && <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">visitor</span>}
                    {t.featured && <Award size={13} className="text-amber-500" />}
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${t.approved ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                      {t.approved ? 'Approved' : 'Pending'}
                    </span>
                  </div>
                  <div className="flex gap-0.5 mb-1.5">
                    {[1, 2, 3, 4, 5].map(s => (
                      <Star key={s} size={12} className={s <= (t.rating || 5) ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/20'} />
                    ))}
                  </div>
                  <p className="font-sans text-sm text-foreground/80 line-clamp-2">"{t.testimonial_text}"</p>
                </div>
                <div className="flex flex-col gap-1.5 flex-shrink-0">
                  <button onClick={() => handleToggleApproval(t.id)} title={t.approved ? 'Hide' : 'Approve'}
                    className={`p-1.5 rounded transition-colors ${t.approved ? 'text-green-600 hover:bg-green-50' : 'text-amber-600 hover:bg-amber-50'}`}
                    data-testid={`toggle-approve-${t.id}`}>
                    {t.approved ? <CheckCircle size={16} /> : <Clock size={16} />}
                  </button>
                  <button onClick={() => handleToggleFeatured(t.id)} title={t.featured ? 'Unfeature' : 'Feature'}
                    className={`p-1.5 rounded transition-colors ${t.featured ? 'text-amber-500 hover:bg-amber-50' : 'text-muted-foreground hover:bg-muted'}`}
                    data-testid={`toggle-feature-${t.id}`}>
                    <Award size={16} />
                  </button>
                  <button onClick={() => handleDelete(t.id)} title="Delete"
                    className="p-1.5 rounded text-red-500 hover:bg-red-50 transition-colors"
                    data-testid={`delete-testimonial-${t.id}`}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TestimonialManager;
